import React from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { Building2, Search, ShieldAlert, ShieldCheck, Users, UserCog } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { errorHandler } from '@/lib/utils';
import { fetchInternalUsers, grantInternalRole, lookupInternalUser, revokeInternalRole, } from '@/services/internal-user';
import { fetchInternalWorkspaces, setInternalWorkspace } from '@/services/internal-workspace';
import { Badge } from '@/components/ui/badge';
const ASSIGNABLE_ROLES = [
    { role: 'ROLE_INTERNAL_EMPLOYEE', label: 'Internal employee' },
    { role: 'ROLE_INTERNAL_ADMIN', label: 'Internal admin' },
];
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/team/')({
    component: TeamPage,
});
function TeamPage() {
    const { user } = useAuthenticationState();
    const { t } = useTranslation('internal');
    const [emailInput, setEmailInput] = React.useState('');
    const [submittedEmail, setSubmittedEmail] = React.useState(null);
    const [override, setOverride] = React.useState(null);
    const { data: staff = [], isLoading: staffLoading } = useQuery({
        queryKey: ['internal-users'],
        queryFn: fetchInternalUsers,
        staleTime: 30_000,
    });
    // useQuery handles the stale-response race: if the user types `a@x` →
    // Search → types `b@x` → Search before the first request resolves, only
    // the latest queryKey is treated as fresh; the stale promise is discarded.
    const { data: lookupResult, isFetching: searching } = useQuery({
        queryKey: ['internal-user-lookup', submittedEmail],
        queryFn: () => lookupInternalUser(submittedEmail),
        enabled: submittedEmail !== null,
        staleTime: 0,
        retry: false,
    });
    if (!isInternalAdmin(user)) {
        return <Navigate to="/console/internal"/>;
    }
    // Mutations update the local override; lookup reflects the server snapshot.
    const target = override ?? lookupResult?.user ?? null;
    const notFound = submittedEmail !== null && !searching && lookupResult?.user === null && override === null;
    const search = () => {
        const email = emailInput.trim();
        if (email === '')
            return;
        setOverride(null);
        setSubmittedEmail(email);
    };
    return (<div className="flex flex-col gap-6 p-6 max-w-3xl mx-auto w-full">
            <div className="space-y-1 border-b pb-4">
                <h1 className="text-3xl font-semibold flex items-center gap-2">
                    <UserCog className="h-7 w-7"/>
                    {t('team.title', { defaultValue: 'Team' })}
                </h1>
                <p className="text-sm text-muted-foreground">
                    {t('team.subtitle', {
            defaultValue: 'Promote or demote internal employees by their email.',
        })}
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Users className="h-4 w-4"/>
                        {t('team.staff_title', { defaultValue: 'Internal staff' })}
                    </CardTitle>
                    <CardDescription>
                        {t('team.staff_desc', {
            defaultValue: 'Everyone with an internal role. Click a row to manage their access.',
        })}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {staffLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : staff.length === 0 ? (<p className="text-sm text-muted-foreground">
                            {t('team.staff_empty', {
                defaultValue: 'No internal staff yet. Promote a user with the form below.',
            })}
                        </p>) : (<ul className="divide-y">
                            {staff.map((member) => {
                const selected = target?.email === member.email;
                return (<li key={member.email ?? member.id ?? member.fullName}>
                                        <button type="button" onClick={() => {
                        setSubmittedEmail(member.email);
                        setOverride(member);
                    }} className={'flex w-full items-center justify-between gap-3 py-3 text-left transition-colors hover:bg-muted/40 px-2 -mx-2 rounded-md ' +
                        (selected ? 'bg-muted/60' : '')}>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium truncate">{member.fullName}</p>
                                                <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                                            </div>
                                            <div className="flex flex-wrap justify-end gap-1">
                                                {member.isSuperAdmin && (<Badge variant="outline" className="text-amber-500 border-amber-500/40">
                                                        <ShieldAlert className="h-3 w-3 mr-1"/>
                                                        Super
                                                    </Badge>)}
                                                {member.roles.includes('ROLE_INTERNAL_ADMIN') && (<Badge variant="secondary">{td(t, 'team.role_admin')}</Badge>)}
                                                {member.roles.includes('ROLE_INTERNAL_EMPLOYEE') && (<Badge variant="outline">{td(t, 'team.role_employee')}</Badge>)}
                                            </div>
                                        </button>
                                    </li>);
            })}
                        </ul>)}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t('team.lookup_title', { defaultValue: 'Find a user' })}</CardTitle>
                    <CardDescription>
                        {t('team.lookup_desc', {
            defaultValue: 'Enter the user’s email to manage their internal roles.',
        })}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 sm:flex-row">
                    <Input type="email" placeholder="user@example.com" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} onKeyDown={(e) => {
            if (e.key === 'Enter')
                search();
        }} autoComplete="off"/>
                    <Button onClick={search} disabled={searching || emailInput.trim() === ''}>
                        <Search className="h-3.5 w-3.5"/>
                        {t('team.search', { defaultValue: 'Search' })}
                    </Button>
                </CardContent>
            </Card>

            {submittedEmail !== null && notFound && (<Card>
                    <CardContent className="py-6 text-sm text-muted-foreground">
                        {t('team.not_found', {
                defaultValue: 'No user found for {{email}}.',
                email: submittedEmail,
            })}
                    </CardContent>
                </Card>)}

            {target && <TargetCard target={target} onUpdate={setOverride}/>}

            <InternalWorkspacesCard />
        </div>);
}
function InternalWorkspacesCard() {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const [publicIdInput, setPublicIdInput] = React.useState('');
    const { data: workspaces = [], isLoading } = useQuery({
        queryKey: ['internal-workspaces'],
        queryFn: fetchInternalWorkspaces,
        staleTime: 30_000,
    });
    const mark = useMutation({
        mutationFn: setInternalWorkspace,
        onSuccess: (workspace) => {
            toast.success(workspace.internal
                ? t('team.workspaces_marked', { name: workspace.name })
                : t('team.workspaces_unmarked', { name: workspace.name }));
            setPublicIdInput('');
            queryClient.invalidateQueries({ queryKey: ['internal-workspaces'] });
            // Assignment-create dropdown sources from /internal/campaigns which
            // filters by Workspace.internal — refresh that cache too.
            queryClient.invalidateQueries({ queryKey: ['internal-campaigns'] });
        },
        onError: errorHandler,
    });
    const submit = () => {
        const publicId = publicIdInput.trim();
        if (publicId === '')
            return;
        mark.mutate({ publicId, enabled: true });
    };
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-4 w-4"/>
                    {t('team.workspaces_title')}
                </CardTitle>
                <CardDescription>{t('team.workspaces_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : workspaces.length === 0 ? (<p className="text-sm text-muted-foreground">{t('team.workspaces_empty')}</p>) : (<ul className="divide-y">
                        {workspaces.map((workspace) => (<li key={workspace.publicId} className="flex items-center justify-between gap-3 py-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{workspace.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{workspace.publicId}</p>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => mark.mutate({ publicId: workspace.publicId, enabled: false })} disabled={mark.isPending}>
                                    {t('team.workspaces_unmark_action')}
                                </Button>
                            </li>))}
                    </ul>)}

                <div className="space-y-2 border-t pt-4">
                    <p className="text-sm font-medium">{t('team.workspaces_mark_label')}</p>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Input value={publicIdInput} onChange={(e) => setPublicIdInput(e.target.value)} onKeyDown={(e) => {
            if (e.key === 'Enter')
                submit();
        }} placeholder="ws_…" autoComplete="off"/>
                        <Button onClick={submit} disabled={mark.isPending || publicIdInput.trim() === ''}>
                            {t('team.workspaces_mark_action')}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>);
}
function TargetCard({ target, onUpdate, }) {
    const queryClient = useQueryClient();
    const { t } = useTranslation('internal');
    const grant = useMutation({
        mutationFn: grantInternalRole,
        onSuccess: (data) => {
            toast.success(data.message);
            onUpdate(data.user);
            queryClient.invalidateQueries({ queryKey: ['internal-users'] });
        },
        onError: errorHandler,
    });
    const revoke = useMutation({
        mutationFn: revokeInternalRole,
        onSuccess: (data) => {
            toast.success(data.message);
            onUpdate(data.user);
            queryClient.invalidateQueries({ queryKey: ['internal-users'] });
        },
        onError: errorHandler,
    });
    // Pending revocation pops a confirm dialog. Granting fires immediately —
    // it's an additive action and easy to reverse with another click.
    const [pendingRevoke, setPendingRevoke] = React.useState(null);
    const handleToggle = (role, label, next) => {
        if (target.email === null)
            return;
        if (next) {
            grant.mutate({ email: target.email, role });
        }
        else {
            setPendingRevoke({ role, label });
        }
    };
    const confirmRevoke = () => {
        if (target.email === null || pendingRevoke === null)
            return;
        revoke.mutate({ email: target.email, role: pendingRevoke.role });
        setPendingRevoke(null);
    };
    const isPending = grant.isPending || revoke.isPending;
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    {target.isSuperAdmin ? (<ShieldAlert className="h-4 w-4 text-amber-500"/>) : (<ShieldCheck className="h-4 w-4"/>)}
                    {target.fullName}
                </CardTitle>
                <CardDescription className="flex flex-wrap gap-2">
                    <span>{target.email}</span>
                    {target.isSelf && <span className="text-amber-500">(you — cannot self-manage)</span>}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {ASSIGNABLE_ROLES.map(({ role, label }) => {
            const has = target.roles.includes(role);
            return (<div key={role} className="flex items-center justify-between border-b pb-3 last:border-b-0 last:pb-0">
                            <div>
                                <p className="text-sm font-medium">{t(`team.role.${role}`, { defaultValue: label })}</p>
                                <p className="text-xs text-muted-foreground">{role}</p>
                            </div>
                            <Switch checked={has} onCheckedChange={(next) => handleToggle(role, label, next)} disabled={isPending || target.isSelf}/>
                        </div>);
        })}

                <AlertDialog open={pendingRevoke !== null} onOpenChange={(open) => !open && setPendingRevoke(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {t('team.revoke_title', {
            role: pendingRevoke?.label ?? '',
            defaultValue: 'Revoke {{role}}?',
        })}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {t('team.revoke_desc', {
            name: target.fullName,
            defaultValue: '{{name}} will lose access to internal features that require this role. They’ll need to sign out and back in for the change to take effect.',
        })}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>{t('team.cancel', { defaultValue: 'Cancel' })}</AlertDialogCancel>
                            <AlertDialogAction onClick={confirmRevoke} className="bg-destructive text-destructive-foreground">
                                {t('team.revoke_action', { defaultValue: 'Revoke' })}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                    {t('team.super_note', {
            defaultValue: 'ROLE_SUPER_ADMIN can only be granted via the CLI for safety. Use `php bin/console adora:user:promote --super <email>`.',
        })}
                </div>
            </CardContent>
        </Card>);
}
